import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { courts as courtsApi, payments, auth } from '../../lib/api';
import { createWebSocketAdapter } from '../../lib/websocket';
import { resolveCreateIntentFeedback } from '../../lib/bookingFeedback';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock,
  CreditCard,
  Filter,
  Map,
  MapPin,
  Search,
  Sparkles,
  Star,
  Waves,
  X,
  Zap,
} from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';


const SURFACES = ['all', 'hard', 'clay', 'grass', 'carpet', 'indoor', 'other'];
const SURFACE_META = {
  hard: {
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
    bar: 'from-blue-400 to-cyan-500',
  },
  clay: {
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    dot: 'bg-orange-500',
    bar: 'from-orange-400 to-amber-500',
  },
  grass: {
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
    bar: 'from-emerald-400 to-lime-500',
  },
  carpet: {
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
    bar: 'from-purple-400 to-violet-600',
  },
  indoor: {
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    dot: 'bg-zinc-500',
    bar: 'from-zinc-400 to-zinc-600',
  },
  other: {
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    dot: 'bg-zinc-400',
    bar: 'from-zinc-300 to-zinc-500',
  },
};

const DURATION_OPTIONS = [30, 60, 90];
const TIMELINE_START = 7;
const TIMELINE_END = 22;
const HOUR_HEIGHT = 48;

const QUICK_SLOTS = [
  { label: '07:00-08:00', start: '07:00', end: '08:00' },
  { label: '18:00-19:30', start: '18:00', end: '19:30' },
];

function timeToMinutes(t) {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}

function snapToSlot(minutes, step = 30) {
  return Math.round(minutes / step) * step;
}

const getSurfaceMeta = (surface) => SURFACE_META[surface] || SURFACE_META.other;

function CourtColumn({ court, bookings, dragState, onMouseDown, onMouseMove, onMouseUp }) {
  const totalMinutes = (TIMELINE_END - TIMELINE_START) * 60;
  const totalHeight = (TIMELINE_END - TIMELINE_START) * HOUR_HEIGHT;
  const surface = getSurfaceMeta(court.surface);

  const toTop = (time) =>
    ((timeToMinutes(time) - TIMELINE_START * 60) / totalMinutes) * totalHeight;

  const toHeight = (start, end) =>
    Math.max(((timeToMinutes(end) - timeToMinutes(start)) / totalMinutes) * totalHeight, 16);

  const isDragging = dragState?.courtId === court.id && dragState?.isDragging;
  let selTop = 0;
  let selHeight = 0;
  if (isDragging && dragState.startMinutes != null && dragState.endMinutes != null) {
    const lo = Math.min(dragState.startMinutes, dragState.endMinutes);
    const hi = Math.max(dragState.startMinutes, dragState.endMinutes);
    selTop = ((lo - TIMELINE_START * 60) / totalMinutes) * totalHeight;
    selHeight = Math.max(((hi - lo) / totalMinutes) * totalHeight, 4);
  }

  const yToMinutes = (y, rect) => {
    const frac = Math.max(0, Math.min(1, y / rect.height));
    return snapToSlot(TIMELINE_START * 60 + frac * totalMinutes);
  };

  return (
    <div className="min-w-[120px] flex-1">
      <div className="mb-2 truncate rounded-xl border border-zinc-100 bg-zinc-50 px-3 py-2 text-center text-xs font-semibold text-zinc-700" title={court.name}>
        <span className={`mr-2 inline-block h-2 w-2 rounded-full ${surface.dot}`} />
        {court.name}
      </div>
      <div
        className="relative select-none rounded-xl border border-zinc-100 bg-white cursor-crosshair"
        style={{ height: totalHeight, touchAction: 'none' }}
        onPointerDown={(e) => {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          const rect = e.currentTarget.getBoundingClientRect();
          e.currentTarget.setPointerCapture(e.pointerId);
          onMouseDown(court.id, yToMinutes(e.clientY - rect.top, rect));
        }}
        onPointerMove={(e) => {
          if (!dragState?.isDragging || dragState.courtId !== court.id) return;
          const rect = e.currentTarget.getBoundingClientRect();
          onMouseMove(yToMinutes(e.clientY - rect.top, rect));
        }}
        onPointerUp={(e) => {
          if (!dragState?.isDragging || dragState.courtId !== court.id) return;
          const rect = e.currentTarget.getBoundingClientRect();
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
          onMouseUp(court, yToMinutes(e.clientY - rect.top, rect));
        }}
        onPointerCancel={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
          }
        }}
      >
        {isDragging && selHeight > 4 && (
          <div
            className="absolute left-1 right-1 z-10 rounded-lg border border-lime-400 bg-lime-300/25 pointer-events-none"
            style={{ top: selTop, height: selHeight }}
          />
        )}
        {bookings.map((slot, index) => (
          <div
            key={`${slot.id || 'slot'}-${slot.start_time}-${index}`}
            className="absolute left-1 right-1 overflow-hidden rounded-lg bg-rose-500 px-2 py-1 text-[10px] font-semibold text-white shadow-sm pointer-events-none"
            style={{ top: toTop(slot.start_time), height: toHeight(slot.start_time, slot.end_time) }}
            title={`${slot.start_time}-${slot.end_time}`}
          >
            {slot.start_time}
          </div>
        ))}
      </div>
    </div>
  );
}

function MultiCourtTimeline({ courts, schedulesByCourt, dragState, onMouseDown, onMouseMove, onMouseUp }) {
  const hours = Array.from({ length: TIMELINE_END - TIMELINE_START + 1 }, (_, i) => TIMELINE_START + i);
  const totalHeight = (TIMELINE_END - TIMELINE_START) * HOUR_HEIGHT;

  if (courts.length === 0) return null;

  return (
    <div className="overflow-x-auto rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex min-w-[720px]">
        <div className="relative w-14 shrink-0 pt-12" style={{ height: totalHeight + 48 }}>
          {hours.map((h) => (
            <div
              key={h}
              className="absolute text-xs font-medium text-zinc-400"
              style={{ top: 48 + (h - TIMELINE_START) * HOUR_HEIGHT - 8 }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-x-0 top-12" style={{ height: totalHeight }}>
            {hours.map((h) => (
              <div
                key={h}
                className="absolute left-0 right-0 border-t border-zinc-100"
                style={{ top: (h - TIMELINE_START) * HOUR_HEIGHT }}
              />
            ))}
          </div>
          <div className="relative z-0 flex gap-3 pt-3">
            {courts.map((court) => (
              <CourtColumn
                key={court.id}
                court={court}
                bookings={schedulesByCourt[court.id] || []}
                dragState={dragState}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DayTimeline({ slots, noBookingsLabel }) {
  const totalMinutes = (TIMELINE_END - TIMELINE_START) * 60;
  const totalHeight = (TIMELINE_END - TIMELINE_START) * HOUR_HEIGHT;

  const toTop = (time) =>
    ((timeToMinutes(time) - TIMELINE_START * 60) / totalMinutes) * totalHeight;

  const toHeight = (start, end) =>
    Math.max(((timeToMinutes(end) - timeToMinutes(start)) / totalMinutes) * totalHeight, 20);

  const hours = Array.from({ length: TIMELINE_END - TIMELINE_START + 1 }, (_, i) => TIMELINE_START + i);

  return (
    <div className="flex gap-2 overflow-y-auto rounded-2xl border border-zinc-100 bg-zinc-50 p-3" style={{ maxHeight: 480 }}>
      <div className="relative w-12 shrink-0" style={{ height: totalHeight }}>
        {hours.map((h) => (
          <div
            key={h}
            className="absolute text-xs font-medium text-zinc-400"
            style={{ top: (h - TIMELINE_START) * HOUR_HEIGHT - 8 }}
          >
            {String(h).padStart(2, '0')}:00
          </div>
        ))}
      </div>
      <div className="relative flex-1 rounded-xl border border-zinc-100 bg-white" style={{ height: totalHeight }}>
        {hours.map((h) => (
          <div
            key={h}
            className="absolute left-0 right-0 border-t border-zinc-100"
            style={{ top: (h - TIMELINE_START) * HOUR_HEIGHT }}
          />
        ))}
        {slots.map((slot, index) => (
          <div
            key={`${slot.id || 'schedule'}-${slot.start_time}-${index}`}
            className="absolute left-2 right-2 overflow-hidden rounded-lg bg-rose-500 px-2 py-1 text-xs font-semibold text-white shadow-sm"
            style={{ top: toTop(slot.start_time), height: toHeight(slot.start_time, slot.end_time) }}
            title={`${slot.start_time} - ${slot.end_time}`}
          >
            <Clock size={10} className="mr-1 inline" />
            {slot.start_time}-{slot.end_time}
          </div>
        ))}
        {slots.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-500">
            {noBookingsLabel}
          </div>
        )}
      </div>
    </div>
  );
}

export const CourtsPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [courtsList, setCourtsList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [scheduleDate, setScheduleDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [courtsLoading, setCourtsLoading] = useState(true);
  const [courtsError, setCourtsError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [multiDate, setMultiDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [multiSchedules, setMultiSchedules] = useState({});
  const [multiLoading, setMultiLoading] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [recommendedSlots, setRecommendedSlots] = useState([]);

  const [bookingData, setBookingData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '10:00',
    end_time: '11:00',
    duration_minutes: 60,
  });

  const [filters, setFilters] = useState({ country: '', city: '', surface: '' });

  const selectedCourtRef = useRef(null);
  const scheduleDateRef = useRef(scheduleDate);
  const scheduleOpenRef = useRef(false);

  useEffect(() => {
    selectedCourtRef.current = selectedCourt;
  }, [selectedCourt]);
  useEffect(() => {
    scheduleDateRef.current = scheduleDate;
  }, [scheduleDate]);
  useEffect(() => {
    scheduleOpenRef.current = showScheduleModal;
  }, [showScheduleModal]);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await courtsApi.getBookings();
      setBookings(Array.isArray(res.data) ? res.data : []);
    } catch {}
  }, []);

  const handleWsMessage = useCallback((data) => {
    const court = selectedCourtRef.current;
    const date = scheduleDateRef.current;
    const isOpen = scheduleOpenRef.current;

    if (data.type === 'court_booking_new') {
      if (isOpen && court?.id === data.court_id && date === data.date) {
        setScheduleSlots((prev) =>
          prev.some((slot) => slot.id === data.booking.id) ? prev : [...prev, data.booking]
        );
        toast.info(t('court.bookSuccess'));
      }
      setMultiSchedules((prev) => {
        const existing = prev[data.court_id] || [];
        if (existing.some((slot) => slot.id === data.booking.id)) return prev;
        return { ...prev, [data.court_id]: [...existing, data.booking] };
      });
      fetchBookings();
    }

    if (data.type === 'court_booking_cancelled') {
      if (isOpen && court?.id === data.court_id && date === data.date) {
        setScheduleSlots((prev) => prev.filter((slot) => slot.id !== data.booking_id));
        toast.info(t('court.cancelSuccess'));
      }
      setMultiSchedules((prev) => {
        const existing = prev[data.court_id] || [];
        return { ...prev, [data.court_id]: existing.filter((slot) => slot.id !== data.booking_id) };
      });
      fetchBookings();
    }
  }, [fetchBookings, t]);

  useEffect(() => {
    if (!user?.id) return;
    let reconnectTimer;
    const adapter = createWebSocketAdapter({
      path: '/ws',
      reconnect: true,
      reconnectInterval: 1500,
      maxReconnectAttempts: 20,
      tokenProvider: async () => {
        const { data } = await auth.getWsToken();
        return data?.token;
      },
      onOpen: () => setWsConnected(true),
      onClose: () => setWsConnected(false),
      onMessage: (evt) => {
        try {
          handleWsMessage(JSON.parse(evt.data));
        } catch {}
      },
      onError: () => {},
    });

    adapter.connect().catch(() => {
      reconnectTimer = setTimeout(() => {
        adapter.connect().catch(() => {});
      }, 3000);
    });

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      adapter.close();
      setWsConnected(false);
    };
  }, [user?.id, handleWsMessage]);

  const fetchCourts = useCallback(async (overrideFilters) => {
    setCourtsLoading(true);
    setCourtsError('');
    try {
      const res = await courtsApi.getAll(overrideFilters ?? filters);
      setCourtsList(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCourtsError(t('court.loadError'));
      toast.error(t('court.loadError'));
    } finally {
      setCourtsLoading(false);
    }
  }, [filters, t]);

  const fetchMultiSchedule = useCallback(async (courts, date) => {
    if (!courts.length) return;
    setMultiLoading(true);
    try {
      const ids = courts.map((court) => court.id).join(',');
      const res = await courtsApi.getMultiSchedule(ids, date);
      const map = {};
      for (const entry of res.data.courts || []) map[entry.court.id] = entry.bookings || [];
      setMultiSchedules(map);
    } catch {
      const map = {};
      await Promise.all(
        courts.map(async (court) => {
          try {
            const res = await courtsApi.getSchedule(court.id, date);
            map[court.id] = res.data.bookings || [];
          } catch {}
        })
      );
      setMultiSchedules(map);
    } finally {
      setMultiLoading(false);
    }
  }, []);

  const fetchRecommended = useCallback(async (date) => {
    try {
      const res = await courtsApi.getRecommendedSlots(date);
      setRecommendedSlots(res.data.recommended_slots || []);
    } catch {
      setRecommendedSlots([]);
    }
  }, []);

  useEffect(() => {
    fetchCourts();
    fetchBookings();
    fetchRecommended(multiDate);
  }, [fetchCourts, fetchBookings, fetchRecommended, multiDate]);

  useEffect(() => {
    if (courtsList.length > 0) fetchMultiSchedule(courtsList, multiDate);
  }, [courtsList, multiDate, fetchMultiSchedule]);

  const loadSchedule = async (courtId, date) => {
    setScheduleLoading(true);
    try {
      const res = await courtsApi.getSchedule(courtId, date);
      setScheduleSlots(res.data.bookings || []);
    } catch {
      toast.error(t('court.scheduleError'));
    } finally {
      setScheduleLoading(false);
    }
  };

  const openSchedule = async (court) => {
    setSelectedCourt(court);
    setShowScheduleModal(true);
    await loadSchedule(court.id, scheduleDate);
  };

  const handleScheduleDateChange = async (e) => {
    const d = e.target.value;
    setScheduleDate(d);
    if (selectedCourt) await loadSchedule(selectedCourt.id, d);
  };

  const handleMultiDateChange = (e) => {
    const d = e.target.value;
    setMultiDate(d);
    fetchRecommended(d);
    if (courtsList.length > 0) fetchMultiSchedule(courtsList, d);
  };

  const openBookingModal = useCallback((court, opts = {}) => {
    setSelectedCourt(court);
    const start = opts.start_time || '10:00';
    const end = opts.end_time || minutesToTime(timeToMinutes(start) + 60);
    const dur = opts.duration_minutes || timeToMinutes(end) - timeToMinutes(start);
    setBookingData({ date: opts.date || multiDate, start_time: start, end_time: end, duration_minutes: dur });
    setShowBookingModal(true);
  }, [multiDate]);

  const handleBook = async (e) => {
    e.preventDefault();
    const endTime = bookingData.end_time || minutesToTime(timeToMinutes(bookingData.start_time) + bookingData.duration_minutes);
    try {
      await payments.createIntent({
        court_id: selectedCourt.id,
        date: bookingData.date,
        start_time: bookingData.start_time,
        end_time: endTime,
      });
      const feedback = resolveCreateIntentFeedback();
      toast[feedback.toastType](t(feedback.messageKey));
      setShowBookingModal(false);
      fetchBookings();
      fetchMultiSchedule(courtsList, multiDate);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('court.loadError'));
    }
  };

  const handleCancel = async (courtId, bookingId) => {
    if (!window.confirm(t('common.cancel') + '?')) return;
    try {
      await courtsApi.cancelBooking(courtId, bookingId);
      toast.success(t('court.cancelSuccess'));
      fetchBookings();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('court.loadError'));
    }
  };

  const displayEndTime = bookingData.end_time || minutesToTime(timeToMinutes(bookingData.start_time) + bookingData.duration_minutes);

  const handleQuickSlot = (court, slot) => {
    openBookingModal(court, {
      date: multiDate,
      start_time: slot.start,
      end_time: slot.end,
      duration_minutes: timeToMinutes(slot.end) - timeToMinutes(slot.start),
    });
  };

  const handleRecommendedSlot = (court, slot) => {
    openBookingModal(court, {
      date: multiDate,
      start_time: slot.start_time,
      end_time: slot.end_time,
      duration_minutes: timeToMinutes(slot.end_time) - timeToMinutes(slot.start_time),
    });
  };

  const handleDragStart = useCallback((courtId, startMinutes) => {
    setDragState({ courtId, startMinutes, endMinutes: startMinutes, isDragging: true });
  }, []);

  const handleDragMove = useCallback((endMinutes) => {
    setDragState((prev) => (prev ? { ...prev, endMinutes } : prev));
  }, []);

  const handleDragEnd = useCallback((court, endMinutes) => {
    setDragState((prev) => {
      if (!prev) return null;
      const lo = Math.min(prev.startMinutes, endMinutes);
      const hi = Math.max(prev.startMinutes, endMinutes);
      if (hi - lo >= 30) {
        openBookingModal(court, {
          date: multiDate,
          start_time: minutesToTime(lo),
          end_time: minutesToTime(hi),
          duration_minutes: hi - lo,
        });
      }
      return null;
    });
  }, [multiDate, openBookingModal]);

  useEffect(() => {
    const cancel = () => setDragState(null);
    window.addEventListener('mouseup', cancel);
    return () => window.removeEventListener('mouseup', cancel);
  }, []);

  const activeSurface = filters.surface || 'all';

  const setSurface = (surface) => {
    const updated = { ...filters, surface: surface === 'all' ? '' : surface };
    setFilters(updated);
    fetchCourts(updated);
  };

  const getSurfaceLabel = (surface) =>
    surface === 'all'
      ? t('court.surfaces.all')
      : t(`court.surfaces.${SURFACE_META[surface] ? surface : 'other'}`);

  const getBookingStatusLabel = (status) =>
    t(`court.bookingStatuses.${status || 'unknown'}`, {
      defaultValue: t('court.bookingStatuses.unknown'),
    });

  const getPaymentStatusLabel = (status) =>
    t(`court.paymentStatuses.${status || 'unpaid'}`, {
      defaultValue: t('court.paymentStatuses.unpaid'),
    });

  const filteredCourts = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return courtsList;
    return courtsList.filter((court) =>
      [court.name, court.address, court.city, court.country, court.surface]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [courtsList, searchTerm]);

  const surfaceCounts = useMemo(() => {
    return courtsList.reduce((counts, court) => {
      const surface = court.surface || 'other';
      counts[surface] = (counts[surface] || 0) + 1;
      return counts;
    }, {});
  }, [courtsList]);

  const nextBooking = useMemo(() => {
    return [...bookings]
      .filter((item) => item.booking?.date)
      .sort((a, b) => new Date(a.booking.date) - new Date(b.booking.date))[0];
  }, [bookings]);

  const summaryCards = [
    {
      label: t('court.title'),
      value: courtsList.length,
      detail: t('court.matchingCurrentFilters'),
      Icon: MapPin,
      color: 'text-zinc-900',
    },
    {
      label: t('court.surfacesTitle'),
      value: Object.keys(surfaceCounts).length,
      detail: t('court.availableTypes'),
      Icon: Waves,
      color: 'text-blue-700',
    },
    {
      label: t('court.myBookings'),
      value: bookings.length,
      detail: nextBooking
        ? t('court.nextBookingDetail', {
            time: nextBooking.booking.start_time,
            court: nextBooking.court.name,
          })
        : t('court.noActiveBookings'),
      Icon: Calendar,
      color: bookings.length ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('court.liveStatus'),
      value: wsConnected ? t('court.live') : t('court.offline'),
      detail: t('court.bookingUpdates'),
      Icon: Zap,
      color: wsConnected ? 'text-emerald-700' : 'text-zinc-600',
    },
  ];

  const renderCourtCard = (court) => {
    const surface = getSurfaceMeta(court.surface);
    return (
      <article
        key={court.id}
        className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-xl hover:shadow-zinc-200/70"
        data-testid={`court-card-${court.id}`}
      >
        <div className={`h-1.5 bg-gradient-to-r ${surface.bar}`} />
        <div className="p-5">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="truncate text-lg font-semibold tracking-tight text-zinc-950">{court.name}</h3>
              <p className="mt-2 flex items-start gap-2 text-sm leading-5 text-zinc-500">
                <MapPin size={15} className="mt-0.5 shrink-0" />
                <span>{[court.address, court.city, court.country].filter(Boolean).join(', ')}</span>
              </p>
            </div>
            <span className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${surface.badge}`}>
              {getSurfaceLabel(court.surface || 'other')}
            </span>
          </div>

          <div className="grid gap-3 rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">{t('common.price')}</span>
              <span className="font-semibold text-zinc-950">
                {court.price_per_hour ? t('court.pricePerHour', { price: court.price_per_hour }) : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-500">{t('court.booking')}</span>
              <span className="font-semibold text-lime-700">{t('court.supported')}</span>
            </div>
          </div>

          <div className="mt-4 flex gap-2">
            {QUICK_SLOTS.map((slot) => (
              <button
                key={slot.label}
                type="button"
                onClick={() => handleQuickSlot(court, slot)}
                className="min-h-11 flex-1 rounded-full border border-zinc-200 px-3 text-xs font-semibold text-zinc-600 transition hover:border-lime-300 hover:bg-lime-50 hover:text-zinc-950"
              >
                {slot.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => openBookingModal(court)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-zinc-950 px-4 text-sm font-semibold text-white transition hover:bg-zinc-800"
              data-testid={`book-court-${court.id}`}
            >
              <CreditCard size={16} />
              {t('court.book')}
            </button>
            <button
              type="button"
              onClick={() => openSchedule(court)}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-zinc-200 px-4 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
              data-testid={`schedule-court-${court.id}`}
            >
              <Calendar size={16} />
              {t('common.date')}
            </button>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="courts-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('court.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('court.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('court.heroBody')}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('court.nextBooking')}</span>
                <Calendar className="text-lime-300" size={21} />
              </div>
              <div className="text-2xl font-semibold tracking-tight text-white">
                {nextBooking ? nextBooking.court.name : t('court.noBooking')}
              </div>
              <p className="mt-2 text-sm text-zinc-300">
                {nextBooking
                  ? `${format(new Date(nextBooking.booking.date), 'dd MMM yyyy')} - ${nextBooking.booking.start_time}-${nextBooking.booking.end_time}`
                  : t('court.bookFromCardBody')}
              </p>
              {wsConnected && (
                <span className="mt-4 inline-flex items-center gap-1 rounded-full border border-lime-300/30 bg-lime-300/10 px-3 py-1 text-xs font-semibold text-lime-200">
                  <Zap size={12} />
                  {t('court.liveUpdates')}
                </span>
              )}
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <Filter size={18} />
                {t('tournament.filters')}
              </div>
              <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_180px_180px_auto] md:items-center">
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('court.searchPlaceholder')}
                    className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 py-3 pl-11 pr-4 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  />
                </label>
                <input
                  type="text"
                  placeholder={t('court.country')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t('court.city')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => fetchCourts()}
                  className="inline-flex min-h-11 items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  data-testid="apply-filters"
                >
                  {t('common.filter')}
                </button>
              </div>
              <div className="mt-4 flex gap-2 overflow-x-auto pb-1">
                {SURFACES.map((surfaceKey) => {
                  const meta = getSurfaceMeta(surfaceKey);
                  return (
                    <button
                      key={surfaceKey}
                      type="button"
                      onClick={() => setSurface(surfaceKey)}
                      className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-semibold transition ${
                        activeSurface === surfaceKey
                          ? 'bg-zinc-950 text-white'
                          : surfaceKey === 'all'
                          ? 'border border-zinc-200 bg-white text-zinc-600 hover:border-lime-300 hover:text-zinc-950'
                          : `border ${meta.badge} hover:opacity-90`
                      }`}
                    >
                      {getSurfaceLabel(surfaceKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            {courtsLoading && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                    <div className="h-1.5 rounded bg-zinc-100" />
                    <div className="mt-5 h-5 w-40 animate-pulse rounded bg-zinc-200" />
                    <div className="mt-4 h-20 animate-pulse rounded-xl bg-zinc-100" />
                  </div>
                ))}
              </div>
            )}

            {!courtsLoading && courtsError && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
                <AlertTriangle className="mx-auto text-rose-600" size={36} />
                <h2 className="mt-4 text-xl font-semibold tracking-tight text-rose-950">{t('court.loadErrorTitle')}</h2>
                <p className="mt-2 text-sm text-rose-700">{courtsError}</p>
                <button
                  type="button"
                  onClick={() => fetchCourts()}
                  className="mt-5 inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
                >
                  {t('common.retry')}
                </button>
              </div>
            )}

            {!courtsLoading && !courtsError && courtsList.length > 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight text-zinc-950">{t('court.multiTimeline')}</h2>
                    <p className="mt-1 text-sm text-zinc-500">{t('court.scheduleDataBody')}</p>
                  </div>
                  <input
                    type="date"
                    className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm font-semibold text-zinc-800 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={multiDate}
                    onChange={handleMultiDateChange}
                  />
                </div>

                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{t('court.quickSlots')}</span>
                  {QUICK_SLOTS.map((slot) => (
                    <button
                      key={slot.label}
                      type="button"
                      onClick={() => {
                        if (courtsList.length === 1) {
                          handleQuickSlot(courtsList[0], slot);
                        } else {
                          toast.info(t('court.selectCourtFirst'));
                        }
                      }}
                      className="inline-flex min-h-9 items-center gap-1 rounded-full border border-lime-200 bg-lime-50 px-3 text-xs font-semibold text-lime-800"
                    >
                      <Clock size={12} />
                      {slot.label}
                    </button>
                  ))}
                </div>

                {recommendedSlots.length > 0 && (
                  <div className="mb-4 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      <Star size={12} className="text-amber-500" />
                      {t('court.recommended')}
                    </span>
                    {recommendedSlots.map((slot, index) => (
                      <button
                        key={`slot-${slot.start_time}-${slot.end_time}-${index}`}
                        type="button"
                        onClick={() => {
                          if (courtsList.length === 1) {
                            handleRecommendedSlot(courtsList[0], slot);
                          } else {
                            toast.info(t('court.selectCourtFirst'));
                          }
                        }}
                        className="inline-flex min-h-9 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-800"
                      >
                        <Sparkles size={12} />
                        {slot.start_time}-{slot.end_time}
                      </button>
                    ))}
                  </div>
                )}

                {multiLoading ? (
                  <div className="flex h-40 items-center justify-center text-sm text-zinc-500">{t('common.loading')}</div>
                ) : (
                  <MultiCourtTimeline
                    courts={filteredCourts}
                    schedulesByCourt={multiSchedules}
                    dragState={dragState}
                    onMouseDown={handleDragStart}
                    onMouseMove={handleDragMove}
                    onMouseUp={handleDragEnd}
                  />
                )}
              </div>
            )}

            {!courtsLoading && !courtsError && courtsList.length === 0 && (
              <div className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center shadow-sm">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
                  <MapPin size={30} />
                </div>
                <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{t('court.emptyTitle')}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">
                  {t('court.emptyBody')}
                </p>
              </div>
            )}

            {!courtsLoading && !courtsError && courtsList.length > 0 && filteredCourts.length === 0 && (
              <div className="rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
                <Search className="mx-auto text-zinc-400" size={34} />
                <h2 className="mt-4 text-xl font-semibold tracking-tight text-zinc-950">{t('court.noMatchingTitle')}</h2>
                <p className="mt-2 text-sm text-zinc-500">{t('court.noMatchingBody')}</p>
              </div>
            )}

            {!courtsLoading && !courtsError && filteredCourts.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {filteredCourts.map(renderCourtCard)}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('court.mapTitle')}</h2>
                  <p className="text-sm text-zinc-500">{t('court.mapUnsupported')}</p>
                </div>
                <Map className="text-zinc-700" size={22} />
              </div>
              <div className="relative h-56 overflow-hidden rounded-2xl border border-dashed border-zinc-300 bg-zinc-100">
                <div className="absolute inset-0 opacity-50">
                  <div className="absolute left-8 top-8 h-px w-56 rotate-12 bg-zinc-300" />
                  <div className="absolute bottom-12 right-6 h-px w-60 -rotate-12 bg-zinc-300" />
                  <div className="absolute left-1/2 top-0 h-full w-px bg-zinc-300" />
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center p-5 text-center">
                  <MapPin className="text-zinc-400" size={34} />
                  <h3 className="mt-3 font-semibold text-zinc-950">{t('court.locationPlaceholder')}</h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-500">
                    {t('court.mapPlaceholderBody')}
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('court.myBookings')}</h2>
                  <p className="text-sm text-zinc-500">{t('court.activeBookingsBody')}</p>
                </div>
                <Calendar className="text-lime-700" size={22} />
              </div>
              {bookings.length > 0 ? (
                <div className="space-y-3">
                  {bookings.map((item) => (
                    <div key={item.booking.id} className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold text-zinc-950">{item.court.name}</h3>
                          <p className="mt-1 text-sm text-zinc-500">
                            <Clock size={12} className="mr-1 inline" />
                            {format(new Date(item.booking.date), 'dd MMM yyyy')} - {item.booking.start_time}-{item.booking.end_time}
                          </p>
                          <p className="mt-2 text-xs font-semibold text-zinc-500">
                            {getBookingStatusLabel(item.booking.status)} / {getPaymentStatusLabel(item.booking.payment_status)}
                          </p>
                        </div>
                        {item.booking.status === 'active' && (
                          <button
                            type="button"
                            onClick={() => handleCancel(item.court.id, item.booking.id)}
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50"
                            title={t('common.cancel')}
                            data-testid={`cancel-booking-${item.booking.id}`}
                          >
                            <X size={17} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center">
                  <Calendar className="mx-auto text-zinc-400" size={32} />
                  <h3 className="mt-3 font-semibold text-zinc-950">{t('court.noBookings')}</h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-500">{t('court.bookingSummaryPlaceholder')}</p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('court.surfaceMix')}</h2>
                  <p className="text-sm text-zinc-500">{t('court.surfaceMixBody')}</p>
                </div>
                <Waves className="text-blue-700" size={22} />
              </div>
              {Object.keys(surfaceCounts).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(surfaceCounts).map(([surfaceKey, count]) => {
                    const meta = getSurfaceMeta(surfaceKey);
                    return (
                      <div key={surfaceKey} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                          <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                          {getSurfaceLabel(surfaceKey)}
                        </span>
                        <span className="text-sm font-semibold text-zinc-950">{count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('court.surfaceBreakdownEmpty')}
                </p>
              )}
            </div>
          </aside>
        </section>
      </div>

      {showBookingModal && selectedCourt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4" data-testid="booking-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-zinc-100 bg-white/95 px-6 py-5 backdrop-blur">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-700">{t('court.bookingSummary')}</p>
                <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">{t('court.book')}</h2>
                <p className="mt-1 text-sm text-zinc-500">{selectedCourt.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowBookingModal(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:bg-zinc-50"
                aria-label={t('court.closeBookingFormAria')}
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleBook} className="space-y-5 p-6">
              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('common.date')}</label>
                <input
                  type="date"
                  className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={bookingData.date}
                  onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                  required
                  data-testid="input-date"
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('schedule.startTime')}</label>
                  <input
                    type="time"
                    className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={bookingData.start_time}
                    onChange={(e) => setBookingData({ ...bookingData, start_time: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('schedule.endTime')}</label>
                  <input
                    type="time"
                    className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                    value={displayEndTime}
                    onChange={(e) => setBookingData({ ...bookingData, end_time: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('common.duration')}</label>
                <div className="grid grid-cols-3 gap-2">
                  {DURATION_OPTIONS.map((duration) => (
                    <button
                      key={duration}
                      type="button"
                      onClick={() => {
                        const end = minutesToTime(timeToMinutes(bookingData.start_time) + duration);
                        setBookingData({ ...bookingData, duration_minutes: duration, end_time: end });
                      }}
                      className={`min-h-11 rounded-full border text-sm font-semibold transition ${
                        bookingData.duration_minutes === duration
                          ? 'border-lime-300 bg-lime-300 text-zinc-950'
                          : 'border-zinc-200 bg-white text-zinc-600 hover:border-lime-300 hover:bg-lime-50'
                      }`}
                    >
                      {duration} {t('common.min')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-zinc-700">{t('common.price')}</span>
                  <span className="text-xl font-semibold text-zinc-950">
                    {selectedCourt.price_per_hour
                      ? `${Math.round(selectedCourt.price_per_hour * bookingData.duration_minutes / 60)} RUB`
                      : '-'}
                  </span>
                </div>
                <p className="mt-2 text-xs leading-5 text-zinc-500">{t('court.stripeComingSoon')}</p>
              </div>
              <div className="flex flex-col-reverse gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => setShowBookingModal(false)}
                  className="inline-flex min-h-11 flex-1 items-center justify-center rounded-full border border-zinc-200 px-5 text-sm font-semibold text-zinc-700 transition hover:bg-zinc-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  data-testid="confirm-booking"
                >
                  <CreditCard size={18} />
                  {t('court.book')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showScheduleModal && selectedCourt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/70 p-4" data-testid="schedule-modal">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-lime-700">{t('court.availability')}</p>
                <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">{t('common.date')}</h2>
                <p className="mt-1 text-sm text-zinc-500">{selectedCourt.name}</p>
              </div>
              <button
                type="button"
                onClick={() => setShowScheduleModal(false)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-zinc-200 text-zinc-600 transition hover:bg-zinc-50"
                aria-label={t('court.closeScheduleAria')}
              >
                <X size={20} />
              </button>
            </div>
            <div className="mb-4">
              <label className="mb-2 block text-sm font-semibold text-zinc-700">{t('common.date')}</label>
              <input
                type="date"
                className="min-h-11 w-full rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 text-zinc-950 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                value={scheduleDate}
                onChange={handleScheduleDateChange}
                data-testid="schedule-date-input"
              />
            </div>
            {scheduleLoading ? (
              <div className="flex h-40 items-center justify-center text-sm text-zinc-500">{t('common.loading')}</div>
            ) : (
              <DayTimeline slots={scheduleSlots} noBookingsLabel={t('court.noBookings')} />
            )}
            <button
              type="button"
              onClick={() => {
                setShowScheduleModal(false);
                openBookingModal(selectedCourt);
              }}
              className="mt-5 inline-flex min-h-11 w-full items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
            >
              {t('court.book')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
